// ============================================
// 2026-10-17（标绘层 S3）：自由线抽稀（RDP）+ Catmull-Rom 拟合 + 生长动画轮廓切片（纯函数，可单测）
// ============================================
import type { P3 } from './primitives';

/** Ramer–Douglas–Peucker 抽稀（按水平 xz 距离；epsilon 单位=米） */
export function rdpSimplify(points: P3[], epsilon = 0.4): P3[] {
  const copy = points.map((p) => [p[0], p[1], p[2]] as P3);
  if (copy.length <= 2) return copy;
  const keep = new Array(copy.length).fill(false);
  keep[0] = keep[copy.length - 1] = true;
  const eps2 = Math.max(0, epsilon) * Math.max(0, epsilon);
  const stack: Array<[number, number]> = [[0, copy.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    if (e - s < 2) continue;
    const a = copy[s];
    const b = copy[e];
    const dx = b[0] - a[0];
    const dz = b[2] - a[2];
    const len2 = dx * dx + dz * dz;
    let maxD = -1;
    let maxI = -1;
    for (let i = s + 1; i < e; i++) {
      const p = copy[i];
      let d2: number;
      if (len2 < 1e-9) {
        d2 = (p[0] - a[0]) ** 2 + (p[2] - a[2]) ** 2;
      } else {
        let t = ((p[0] - a[0]) * dx + (p[2] - a[2]) * dz) / len2;
        t = Math.max(0, Math.min(1, t));
        const px = a[0] + t * dx;
        const pz = a[2] + t * dz;
        d2 = (p[0] - px) ** 2 + (p[2] - pz) ** 2;
      }
      if (d2 > maxD) { maxD = d2; maxI = i; }
    }
    if (maxD > eps2 && maxI > 0) {
      keep[maxI] = true;
      stack.push([s, maxI], [maxI, e]);
    }
  }
  return copy.filter((_, i) => keep[i]);
}

/** Catmull-Rom 样条（均匀参数化；≥3 点平滑过点；<3 点原样返回） */
export function catmullRomCurve(points: P3[], segmentsPerSpan = 12): P3[] {
  const copy = points.map((p) => [p[0], p[1], p[2]] as P3);
  if (copy.length < 3) return copy;
  const n = copy.length;
  const get = (i: number) => copy[Math.max(0, Math.min(n - 1, i))];
  const out: P3[] = [];
  const segs = Math.max(2, Math.floor(segmentsPerSpan));
  for (let i = 0; i < n - 1; i++) {
    const p0 = get(i - 1);
    const p1 = get(i);
    const p2 = get(i + 1);
    const p3 = get(i + 2);
    for (let s = 0; s < segs; s++) {
      const t = s / segs;
      const t2 = t * t;
      const t3 = t2 * t;
      const fx = 0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      const fy = 0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
      const fz = 0.5 * (2 * p1[2] + (-p0[2] + p2[2]) * t + (2 * p0[2] - 5 * p1[2] + 4 * p2[2] - p3[2]) * t2 + (-p0[2] + 3 * p1[2] - 3 * p2[2] + p3[2]) * t3);
      out.push([fx, fy, fz]);
    }
  }
  out.push([copy[n - 1][0], copy[n - 1][1], copy[n - 1][2]]);
  return out;
}

/** 沿折线按累计长度比例切片（生长动画；t<=0 → []，t>=1 → 全量） */
export function sliceOutlineByProgress(outline: P3[], t: number): P3[] {
  if (outline.length === 0) return [];
  if (t <= 0) return [];
  if (t >= 1) return outline.map((p) => [p[0], p[1], p[2]] as P3);
  if (outline.length === 1) return [[outline[0][0], outline[0][1], outline[0][2]]];
  const seg: number[] = [];
  let total = 0;
  for (let i = 1; i < outline.length; i++) {
    const d = Math.hypot(
      outline[i][0] - outline[i - 1][0],
      outline[i][1] - outline[i - 1][1],
      outline[i][2] - outline[i - 1][2],
    );
    seg.push(d);
    total += d;
  }
  if (total <= 1e-9) return [[outline[0][0], outline[0][1], outline[0][2]]];
  const target = total * t;
  const out: P3[] = [[outline[0][0], outline[0][1], outline[0][2]]];
  let acc = 0;
  for (let i = 0; i < seg.length; i++) {
    if (acc + seg[i] < target - 1e-9) {
      out.push([outline[i + 1][0], outline[i + 1][1], outline[i + 1][2]]);
      acc += seg[i];
    } else {
      const r = Math.max(0, Math.min(1, (target - acc) / (seg[i] || 1)));
      if (r > 1e-9) {
        out.push([
          outline[i][0] + (outline[i + 1][0] - outline[i][0]) * r,
          outline[i][1] + (outline[i + 1][1] - outline[i][1]) * r,
          outline[i][2] + (outline[i + 1][2] - outline[i][2]) * r,
        ]);
      }
      break;
    }
  }
  return out;
}
